import { useState, useEffect } from 'react';
import { salaryAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageSquare, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const s = Math.floor((now - d) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function getInitials(name) {
  return (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const roleBg = {
  'Super Admin': 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'Admin': 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'Payroll Auditor': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export function SettlementComments({ recordId }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await salaryAPI.getSettlementComments(recordId);
      setComments(res.data || []);
      setLoaded(true);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open && !loaded) load();
  }, [open]);

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await salaryAPI.addSettlementComment(recordId, text.trim());
      setText('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add comment');
    }
    setSending(false);
  };

  return (
    <div className="border-t border-border" data-testid={`comments-section-${recordId}`}>
      {/* Collapsible header */}
      <button
        className="flex items-center justify-between w-full px-5 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer text-left"
        onClick={() => setOpen(v => !v)}
        data-testid="comments-collapsible-trigger"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" />
          Follow-up Comments
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300" data-testid="comments-count-badge">
            {comments.length}
          </span>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Collapsible body */}
      {open && (
        <div className="px-5 pb-4">
          {/* Comments list */}
          <div className="space-y-0 max-h-64 overflow-y-auto" data-testid="comment-item-list">
            {comments.length === 0 && (
              <p className="text-xs text-muted-foreground py-3">No comments yet. Be the first to add a follow-up note.</p>
            )}
            {comments.map((c) => (
              <div key={c.id} className="flex gap-3 py-2.5 group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 -mx-2 px-2 rounded transition-colors" data-testid={`comment-item-${c.id}`}>
                <div className="shrink-0 w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300 mt-0.5">
                  {getInitials(c.author_name)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-foreground truncate">{c.author_name}</span>
                    <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-1.5 py-0.5 rounded ${roleBg[c.author_role] || 'bg-slate-200/50 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300'}`}>
                      {c.author_role}
                    </span>
                    <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                  </div>
                  <p className="text-sm text-foreground/90 leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div className="relative mt-3 flex items-center" data-testid="comment-input-area">
            <Input
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Add a note about status, follow-up, action taken..."
              className="w-full pr-10 bg-background/50 text-sm"
              aria-label="Add a follow-up comment"
              data-testid="comment-input-field"
            />
            <Button
              size="icon"
              variant="ghost"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground active:scale-95 transition-transform"
              onClick={handleSend}
              disabled={sending || !text.trim()}
              data-testid="send-comment-button"
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
